// filterMilestoneByPlanningSummary.js - OPTIMIZED VERSION with proper exports

// Cache for milestone data to avoid repeated calculations
const milestoneCache = new Map();
export const initiativeHistoryCache = new Map(); // Export this for use in simplified version
const MAX_CACHE_SIZE = 500;

// Clear cache when needed (call this when initiatives data changes)
export const clearMilestonePlanningCache = () => {
  milestoneCache.clear();
  initiativeHistoryCache.clear();
};

// Build initiative history index for O(1) lookups
const buildInitiativeHistoryIndex = (initiativesArray) => {
  if (!initiativesArray || !Array.isArray(initiativesArray)) return new Map();

  const historyIndex = new Map();

  for (const initiative of initiativesArray) {
    if (initiative.history && Array.isArray(initiative.history)) {
      for (const historyItem of initiative.history) {
        const milestoneNumber = historyItem.number;
        if (milestoneNumber) {
          if (!historyIndex.has(milestoneNumber)) {
            historyIndex.set(milestoneNumber, []);
          }
          historyIndex.get(milestoneNumber).push({
            ...historyItem,
            initiativeId: initiative._id
          });
        }
      }
    }
  }

  return historyIndex;
};

// Get milestone history efficiently
const getMilestoneHistory = (milestoneNumber, historyIndex) => {
  return historyIndex.get(milestoneNumber) || [];
};

// Extract actual dates from history with caching
const getActualDatesFromHistory = (milestoneNumber, historyIndex) => {
  const cacheKey = `actual_dates_${milestoneNumber}`;
  if (milestoneCache.has(cacheKey)) {
    return milestoneCache.get(cacheKey);
  }

  let actualStartDate = null;
  let actualEndDate = null;

  const historyEntries = getMilestoneHistory(milestoneNumber, historyIndex);

  if (historyEntries.length > 0) {
    // Find actual start date (status changed from 'Not Started' to 'Started')
    const startEntry = historyEntries.find(
      h => h.changes?.status?.previous === 'Not Started' && h.changes?.status?.new === 'Started'
    );
    if (startEntry) actualStartDate = new Date(startEntry.timestamp);

    // Find actual completion date (status changed from 'Started' to 'Completed')
    const completeEntry = historyEntries.find(
      h => h.changes?.status?.previous === 'Started' && h.changes?.status?.new === 'Completed'
    );
    if (completeEntry) actualEndDate = new Date(completeEntry.timestamp);
  }

  const result = { actualStartDate, actualEndDate };

  // Manage cache size
  if (milestoneCache.size >= MAX_CACHE_SIZE) {
    const firstKey = milestoneCache.keys().next().value;
    milestoneCache.delete(firstKey);
  }
  milestoneCache.set(cacheKey, result);

  return result;
};

// Optimized startOfDay with memoization
const startOfDayCache = new Map();
const startOfDay = (date) => {
  if (!date) return null;

  const dateKey = date instanceof Date ? date.toISOString().split('T')[0] : String(date);
  if (startOfDayCache.has(dateKey)) {
    return startOfDayCache.get(dateKey);
  }

  const dateObj = date instanceof Date ? date : new Date(date);
  const result = new Date(dateObj.getFullYear(), dateObj.getMonth(), dateObj.getDate());

  if (startOfDayCache.size >= 100) {
    const firstKey = startOfDayCache.keys().next().value;
    startOfDayCache.delete(firstKey);
  }
  startOfDayCache.set(dateKey, result);

  return result;
};

// Main filter function - OPTIMIZED
const filterMilestoneByPlanningSummary = (milestone, initiativesArray, currentFilter) => {
  // Early returns for performance
  if (!milestone || !currentFilter || currentFilter.length === 0) return false;

  // Fast path for "Select all" cases
  if (currentFilter.includes('Select all') || currentFilter.length > 20) {
    const commonFilters = ['Completed', 'On plan', 'Not started', 'Late'];
    const hasAllCommon = commonFilters.every(f => currentFilter.includes(f));
    if (hasAllCommon) return true;
  }

  const milestoneStatus = milestone.status;

  // Quick terminal state checks (no date calculations needed)
  if (milestoneStatus === 'Cancelled') {
    return currentFilter.includes('Cancelled');
  }

  if (milestoneStatus === 'Not planned') {
    return currentFilter.includes('Not planned');
  }

  // Build or get history index
  let historyIndex = initiativeHistoryCache.get('index');
  if (!historyIndex || historyIndex.size === 0) {
    historyIndex = buildInitiativeHistoryIndex(initiativesArray);
    initiativeHistoryCache.set('index', historyIndex);
  }

  // Get actual dates from history
  const { actualStartDate, actualEndDate } = getActualDatesFromHistory(milestone.milestoneNumber, historyIndex);

  // Handle missing dates
  const milestoneStartDate = milestone.startDate ? new Date(milestone.startDate) : null;
  const milestoneEndDate = milestone.endDate ? new Date(milestone.endDate) : null;

  if (!milestoneStartDate || !milestoneEndDate) {
    return currentFilter.includes('<not set>');
  }

  // Get current date at start of day
  const currentDate = startOfDay(new Date());

  // Normalize planned dates
  const normalizedStartDate = startOfDay(milestoneStartDate);
  const normalizedEndDate = startOfDay(milestoneEndDate);

  // Normalize actual dates if they exist
  const normalizedActualStartDate = actualStartDate ? startOfDay(actualStartDate) : null;
  const normalizedActualEndDate = actualEndDate ? startOfDay(actualEndDate) : null;

  const msInDay = 24 * 60 * 60 * 1000;

  // Pre-calculate all date differences once
  const daysUntilEnd = normalizedEndDate >= currentDate
    ? Math.ceil((normalizedEndDate.getTime() - currentDate.getTime()) / msInDay)
    : null;
  const daysSinceEnd = currentDate > normalizedEndDate
    ? Math.floor((currentDate.getTime() - normalizedEndDate.getTime()) / msInDay)
    : null;
  const daysUntilStart = normalizedStartDate >= currentDate
    ? Math.ceil((normalizedStartDate.getTime() - currentDate.getTime()) / msInDay)
    : null;
  const daysSinceStart = currentDate > normalizedStartDate
    ? Math.floor((currentDate.getTime() - normalizedStartDate.getTime()) / msInDay)
    : null;

  // Calculate days late
  const daysLateStart = normalizedActualStartDate && normalizedStartDate && normalizedActualStartDate > normalizedStartDate
    ? Math.floor((normalizedActualStartDate.getTime() - normalizedStartDate.getTime()) / msInDay)
    : null;
  const daysLateEnd = normalizedActualEndDate && normalizedEndDate && normalizedActualEndDate > normalizedEndDate
    ? Math.floor((normalizedActualEndDate.getTime() - normalizedEndDate.getTime()) / msInDay)
    : null;

  // Helper functions (defined once)
  const isLatelyStarted = () => {
    return milestoneStatus === 'Started' &&
      normalizedActualStartDate &&
      normalizedStartDate &&
      normalizedActualStartDate > normalizedStartDate;
  };

  const isLatelyCompleted = () => {
    return milestoneStatus === 'Completed' &&
      normalizedActualEndDate &&
      normalizedEndDate &&
      normalizedActualEndDate > normalizedEndDate;
  };

  // Create filter Set for O(1) lookups
  const filterSet = new Set(currentFilter);

  // --- HANDLE COMPLETED MILESTONES ---
  if (milestoneStatus === 'Completed') {
    // Lately Completed (takes precedence)
    if (isLatelyCompleted()) {
      if (daysLateEnd < 7 && filterSet.has('Lately Completed (less than 1 week)')) {
        return true;
      } else if (daysLateEnd >= 7 && daysLateEnd <= 28 && filterSet.has('Lately Completed (1 to 4 weeks)')) {
        return true;
      } else if (daysLateEnd > 28 && filterSet.has('Lately Completed (more than 4 weeks)')) {
        return true;
      }
      return false;
    }

    // Lately Started but Completed on Time
    if (isLatelyStarted()) {
      if (daysLateStart < 7 && filterSet.has('Lately Started (less than 1 week)')) {
        return true;
      } else if (daysLateStart >= 7 && daysLateStart <= 28 && filterSet.has('Lately Started (1 to 4 weeks)')) {
        return true;
      } else if (daysLateStart > 28 && filterSet.has('Lately Started (more than 4 weeks)')) {
        return true;
      }
      return false;
    }

    // Regular Completed
    return filterSet.has('Completed');
  }

  // --- HANDLE LATE TO FINISH (End date passed and not completed) ---
  if (normalizedEndDate && currentDate > normalizedEndDate && milestoneStatus !== 'Completed') {
    if (daysSinceEnd > 28 && filterSet.has('Late (more than 4 weeks)')) {
      return true;
    } else if (daysSinceEnd >= 7 && daysSinceEnd <= 28 && filterSet.has('Late (1 to 4 weeks)')) {
      return true;
    } else if (daysSinceEnd >= 1 && daysSinceEnd < 7 && filterSet.has('Late (less than 1 week)')) {
      return true;
    }
    return false;
  }

  // --- HANDLE STARTED MILESTONES ---
  if (milestoneStatus === 'Started') {
    // Priority 1: Due Soon based on end date (within 30 days)
    if (daysUntilEnd !== null && daysUntilEnd <= 30) {
      if (daysUntilEnd <= 7) {
        if (filterSet.has('Due soon (7 days)') || filterSet.has('Due soon')) {
          return true;
        }
      } else if (daysUntilEnd <= 30) {
        if (filterSet.has('Due soon (30 days)') || filterSet.has('Due soon for 30 days')) {
          return true;
        }
      }
      return false;
    }

    // Priority 2: Lately Started (end date > 30 days away AND started late)
    if (isLatelyStarted()) {
      if (daysLateStart < 7 && filterSet.has('Lately Started (less than 1 week)')) {
        return true;
      } else if (daysLateStart >= 7 && daysLateStart <= 28 && filterSet.has('Lately Started (1 to 4 weeks)')) {
        return true;
      } else if (daysLateStart > 28 && filterSet.has('Lately Started (more than 4 weeks)')) {
        return true;
      }
      return false;
    }

    // Priority 3: On Plan
    return filterSet.has('On plan');
  }

  // --- HANDLE NOT STARTED MILESTONES ---
  if (milestoneStatus === 'Not Started') {
    // Priority 1: Late to Finish (end date passed)
    if (normalizedEndDate && currentDate > normalizedEndDate) {
      return filterSet.has('Late');
    }

    // Priority 2: Due Soon based on start date (within 30 days)
    if (daysUntilStart !== null && daysUntilStart <= 30) {
      if (daysUntilStart <= 7) {
        if (filterSet.has('Due soon (7 days)') || filterSet.has('Due soon')) {
          return true;
        }
      } else if (daysUntilStart <= 30) {
        if (filterSet.has('Due soon (30 days)') || filterSet.has('Due soon for 30 days')) {
          return true;
        }
      }
      return false;
    }

    // Priority 3: Late to Start (start date has passed)
    if (normalizedStartDate && currentDate > normalizedStartDate &&
      normalizedEndDate && currentDate < normalizedEndDate) {
      if (daysSinceStart < 7 && filterSet.has('Late to start (less than 1 week)')) {
        return true;
      } else if (daysSinceStart >= 7 && daysSinceStart <= 28 && filterSet.has('Late to start (1 to 4 weeks)')) {
        return true;
      } else if (daysSinceStart > 28 && filterSet.has('Late to start (more than 4 weeks)')) {
        return true;
      }
      return filterSet.has('Late to start');
    }

    // Priority 4: On Plan (start date > 30 days away)
    if (daysUntilStart !== null && daysUntilStart > 30 && filterSet.has('On plan')) {
      return true;
    }

    return filterSet.has('Not started');
  }

  return false;
};

// Batch processing function for multiple milestones
export const filterMilestonesByPlanningSummaryBatch = (milestones, initiativesArray, currentFilter) => {
  if (!milestones || !Array.isArray(milestones) || milestones.length === 0) return [];
  if (!currentFilter || currentFilter.length === 0) return milestones;

  // Pre-build history index once for all milestones
  const historyIndex = buildInitiativeHistoryIndex(initiativesArray);
  initiativeHistoryCache.set('index', historyIndex);

  // Filter all milestones
  return milestones.filter(milestone =>
    filterMilestoneByPlanningSummary(milestone, initiativesArray, currentFilter)
  );
};

export default filterMilestoneByPlanningSummary;